import { useEffect, useState } from "react";
import { Smartphone, X, Share, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "pula_pwa_dismissed";

function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === "1") return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari has no beforeinstallprompt — show manual guidance
    let t;
    if (isIOS()) t = setTimeout(() => setVisible(true), 2500);

    const onInstalled = () => { setVisible(false); localStorage.setItem(DISMISS_KEY, "1"); };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = () => { setVisible(false); localStorage.setItem(DISMISS_KEY, "1"); };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice.catch(() => null);
      setDeferred(null);
      setVisible(false);
      if (choice?.outcome !== "accepted") localStorage.setItem(DISMISS_KEY, "1");
    } else if (isIOS()) {
      setIosHelp(true);
    }
  };

  if (!visible) return null;

  return (
    <div data-testid="pwa-install-banner"
      className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 w-[92vw] max-w-md animate-fade-up">
      <div className="pula-card p-4 shadow-lg flex items-start gap-3 bg-white">
        <span className="w-11 h-11 rounded-xl bg-pula-green grid place-items-center shrink-0">
          <Smartphone className="w-5 h-5 text-white" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-sm text-pula-ink">Add Pula to your home screen</p>
          {iosHelp ? (
            <p className="text-xs text-pula-inksoft font-body mt-1 flex items-center flex-wrap gap-1">
              Tap <Share className="w-3.5 h-3.5 inline" /> Share, then <Plus className="w-3.5 h-3.5 inline" /> “Add to Home Screen”.
            </p>
          ) : (
            <p className="text-xs text-pula-inksoft font-body mt-1">Works like an app — quick, offline-friendly, one tap away.</p>
          )}
          {!iosHelp && (
            <Button data-testid="pwa-install-btn" onClick={install} size="sm"
              className="mt-2 rounded-full bg-pula-green hover:bg-pula-greenhover text-white h-8 text-xs">
              {isIOS() && !deferred ? "How to add" : "Add to Home Screen"}
            </Button>
          )}
        </div>
        <button data-testid="pwa-dismiss" onClick={dismiss} className="p-1 text-pula-inksoft hover:text-pula-ink transition-colors shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
